/**
 * Hook to manage cursors and camera directly with MapLibre
 * Replaces NavigationPointAnimator + cursor update logic
 */

import maplibregl from "maplibre-gl";
import { useCallback, useEffect, useRef, type MutableRefObject } from "react";
import { FPS_LIMITATION } from "../config/global.configuration";
import { compassCalibrationService, gpsPredictionService, rawCompassDataService, rawGpsDataService } from "../services/navigation";
import { mapReadySlice, navigationSessionSlice, useEphemeralSelector, useMapInstance } from "../store/ephemeral";
import { calculateBearing, getNearestRoadBearing } from "../tools/NavigationTools";
import { NavigationMode, RawGpsData, type CameraConfig, type RouteSegment } from "../types/Navigation";
import { useAnimationFrame } from "./useAnimationFrame";

/** Unique callback ID for navigation animation in the orchestrator */
const NAVIGATION_ANIMATION_CALLBACK_ID = "navigation-animation";

export interface UseMapNavigationProps {
    "handleAnimation": boolean;

    /** When set, cone updates are skipped while ref.current is true (sync freeze on pointerdown). */
    "isInteractingRef"?: MutableRefObject<boolean>;
}

export interface UseMapNavigationReturn {

    /** Update the camera */
    "updateCamera": ( config: Partial<CameraConfig> ) => void;

    /** Update the user position marker */
    "updateUserPositionMarker": ( position: [number, number], heading: number ) => void;

    /** Update the navigation cursor */
    "updateNavigationCursor": ( position: [number, number], heading: number ) => void;

    /** Handle click on user marker */
    "handleUserMarkerClick": ( map: maplibregl.Map ) => void;

    /** Current navigation animation FPS */
    "navigationFps": number;

    /** Maximum configured navigation animation FPS */
    "maxNavigationFps": number;
}

/**
 * Hook to manage cursors and camera with MapLibre
 */
export const useMapNavigation = ( { handleAnimation, isInteractingRef }: UseMapNavigationProps ): UseMapNavigationReturn => {
    const lastCameraConfigRef = useRef<CameraConfig | null>( null );
    const lastUserPositionRef = useRef<[number, number] | null>( null );
    const lastNavigationPositionRef = useRef<[number, number] | null>( null );

    const map = useMapInstance();
    const isMapReady = useEphemeralSelector( mapReadySlice, ( ready ) => ready.isMapReady );
    const navigationMode = useEphemeralSelector( navigationSessionSlice, ( session ) => session.navigationMode );
    const isFollowingNavigation = useEphemeralSelector( navigationSessionSlice, ( session ) => session.isFollowingNavigation );

    const lastNavigationModeRef = useRef<NavigationMode | null>( navigationMode );

    const lastGpsValueRef = useRef<RawGpsData | null>( null );

    const maxNavigationFps = FPS_LIMITATION.navigationAnimation;

    // Camera update
    const updateCamera = useCallback(
        ( partialConfig: Partial<CameraConfig> ) => {
            if ( !map || !isMapReady ) {
                return;
            }

            // Get current camera state
            const currentCenter = map.getCenter();
            const currentBearing = map.getBearing();
            const currentPitch = map.getPitch();
            const currentZoom = map.getZoom();

            // Merge partial config with last config to get complete config
            const lastConfig = lastCameraConfigRef.current;
            const config: CameraConfig = {
                "center":   partialConfig.center ?? lastConfig?.center ?? [ currentCenter.lng, currentCenter.lat ],
                "zoom":     partialConfig.zoom ?? lastConfig?.zoom ?? currentZoom,
                "bearing":  partialConfig.bearing ?? lastConfig?.bearing ?? currentBearing,
                "pitch":    partialConfig.pitch ?? lastConfig?.pitch ?? currentPitch,
                "duration": partialConfig.duration ?? lastConfig?.duration ?? 1000,
            };

            // Detect what has changed
            const bearingChanged = Math.abs( config.bearing - currentBearing ) > 0.5;
            const positionChanged =
                config.center[0] !== currentCenter.lng || config.center[1] !== currentCenter.lat;
            const pitchChanged = Math.abs( config.pitch - currentPitch ) > 0.1;
            const zoomChanged = Math.abs( config.zoom - currentZoom ) > 0.1;

            if ( bearingChanged && !positionChanged && !pitchChanged && !zoomChanged ) {
                // Only heading changes - instant rotation
                map.setBearing( config.bearing );
                map.easeTo( {
                    "bearing":  config.bearing,
                    "duration": config.duration,
                } );
            } else {
                // Position, pitch or zoom changes - use easing
                map.easeTo( {
                    "center":   config.center,
                    "zoom":     config.zoom,
                    "bearing":  config.bearing,
                    "pitch":    config.pitch,
                    "duration": config.duration,
                } );
            }

            // Store the complete config
            lastCameraConfigRef.current = config;
        },
        [ map, isMapReady ],
    );

    // Update user position marker
    const updateUserPositionMarker = useCallback(
        ( position: [number, number], heading: number ) => {
            if ( !map || !isMapReady ) {
                return;
            }
            if ( !map.isStyleLoaded() ) {
                return;
            }

            // Update user-heading-marker source (cone with direction)
            const headingSource = map.getSource( "user-heading-marker" ) as maplibregl.GeoJSONSource;
            if ( headingSource !== null && headingSource !== undefined ) {
                headingSource.setData( {
                    "type":     "FeatureCollection",
                    "features": [
                        {
                            "type":     "Feature",
                            "geometry": {
                                "type":        "Point",
                                "coordinates": position,
                            },
                            "properties": { "bearing": heading },
                        },
                    ],
                } );
            }

            // Update user-location source (simple point)
            const userLocationSource = map.getSource( "user-location" ) as maplibregl.GeoJSONSource;
            if ( userLocationSource !== null && userLocationSource !== undefined ) {
                userLocationSource.setData( {
                    "type":     "FeatureCollection",
                    "features": [
                        {
                            "type":     "Feature",
                            "geometry": {
                                "type":        "Point",
                                "coordinates": position,
                            },
                            "properties": { "bearing": heading },
                        },
                    ],
                } );
            }

            lastUserPositionRef.current = position;

            /*
             * console.log( "📍 [useMapNavigation] User position marker updated", {
             *     position,
             *     heading,
             * } );
             */
        },
        [ map, isMapReady ],
    );

    // Update navigation cursor
    const updateNavigationCursor = useCallback(
        ( position: [number, number], heading: number ) => {
            /*
             * console.log( "🎯 [useMapNavigation] updateNavigationCursor called", {
             *     position,
             *     heading,
             *     "mapExists": map !== null,
             *     "timestamp": new Date().toISOString(),
             *  } );
             */

            if ( !map || !isMapReady ) {
                return;
            }
            if ( !map.isStyleLoaded() ) {
                return;
            }

            const source = map.getSource( "navigation-animated" ) as maplibregl.GeoJSONSource;
            if ( source === null || source === undefined ) {
                // console.log( "❌ [useMapNavigation] Navigation source not found" );

                return;
            }

            // console.log( "✅ [useMapNavigation] Navigation source found, updating data" );
            source.setData( {
                "type":     "FeatureCollection",
                "features": [
                    {
                        "type":     "Feature",
                        "geometry": {
                            "type":        "Point",
                            "coordinates": position,
                        },
                        "properties": { "bearing": heading },
                    },
                ],
            } );

            lastNavigationPositionRef.current = position;

            /*
             * console.log( "✅ [useMapNavigation] Navigation cursor updated successfully", {
             *    position,
             *    heading,
             * } );
             */
        },
        [ map, isMapReady ],
    );

    // Extract route segments from MapLibre (only around user position, closest LineString for performance)
    const extractRouteSegmentsFromMap = useCallback( ( map: maplibregl.Map, position: [number, number] ) => {
        try {
            const radiusMeters = 120;
            const radiusDegrees = radiusMeters / 111000;

            const bboxTopLeft: [number, number] = [
                position[0] - radiusDegrees,
                position[1] + radiusDegrees,
            ];
            const bboxBottomRight: [number, number] = [
                position[0] + radiusDegrees,
                position[1] - radiusDegrees,
            ];

            const topLeft = map.project( bboxTopLeft );
            const bottomRight = map.project( bboxBottomRight );

            const features = map.queryRenderedFeatures( [
                [ topLeft.x, topLeft.y ],
                [ bottomRight.x, bottomRight.y ],
            ] );

            const maxDistanceDegrees = 0.0009;

            let closestFeature: ( typeof features )[number] | null = null;
            let closestDistance = Infinity;

            for ( const feature of features ) {
                if ( feature.geometry.type !== "LineString" ) {
                    continue;
                }

                const coordinates = ( feature.geometry as unknown as { "coordinates": [number, number][] } ).coordinates;

                if ( coordinates.length === 0 ) {
                    continue;
                }

                let minDistance = Infinity;
                for ( let i = 0; i < coordinates.length - 1; i++ ) {
                    const start = coordinates[i];
                    const end = coordinates[i + 1];

                    if ( start === undefined || end === undefined ) {
                        continue;
                    }

                    const dx = end[0] - start[0];
                    const dy = end[1] - start[1];
                    const lengthSq = dx * dx + dy * dy;

                    if ( lengthSq === 0 ) {
                        const dist = Math.sqrt( Math.pow( position[0] - start[0], 2 ) + Math.pow( position[1] - start[1], 2 ) );
                        minDistance = Math.min( minDistance, dist );
                    } else {
                        const t = Math.max( 0, Math.min( 1, ( ( position[0] - start[0] ) * dx + ( position[1] - start[1] ) * dy ) / lengthSq ) );
                        const proj = [ start[0] + t * dx, start[1] + t * dy ];
                        const dist = Math.sqrt( Math.pow( position[0] - proj[0], 2 ) + Math.pow( position[1] - proj[1], 2 ) );
                        minDistance = Math.min( minDistance, dist );
                    }
                }

                if ( minDistance < maxDistanceDegrees && minDistance < closestDistance ) {
                    closestDistance = minDistance;
                    closestFeature = feature;
                }
            }

            if ( closestFeature === null ) {
                return [];
            }

            const coordinates = ( closestFeature.geometry as unknown as { "coordinates": [number, number][] } ).coordinates;
            const routeSegments: RouteSegment[] = [];

            for ( let i = 0; i < coordinates.length - 1; i++ ) {
                const start = coordinates[i];
                const end = coordinates[i + 1];

                if ( start !== undefined && end !== undefined ) {
                    const bearing = calculateBearing(
                        start[1],
                        start[0],
                        end[1],
                        end[0],
                    );
                    const length = Math.sqrt( Math.pow( end[0] - start[0], 2 ) + Math.pow( end[1] - start[1], 2 ) ) * 111000;

                    routeSegments.push( {
                        start,
                        end,
                        bearing,
                        length,
                    } );
                }
            }

            return routeSegments;
        } catch( error ) {
            console.error( "❌ [useMapNavigation] Error extracting route segments from map:", error );

            return [];
        }
    }, [ ] );

    // Animation callback for navigation updates (FPS tracking handled by AnimationOrchestrator)
    const navigationAnimationCallback = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ( _deltaTime: number, _now: number ): void => {
            if ( !map ) {
                return;
            }

            // Update UI based on navigation mode
            let rawGps = rawGpsDataService.getLastValue();
            const rawCompass = rawCompassDataService.getLastValue();

            if ( !isFollowingNavigation ) {
                const rawHeading = rawCompass.heading ?? 0;
                const interacting = isInteractingRef?.current === true;

                if ( !interacting ) {
                    updateUserPositionMarker( [ rawGps.longitude, rawGps.latitude ], rawHeading );
                }
                lastGpsValueRef.current = rawGps;
            } else {
                const prev = lastGpsValueRef.current;
                if ( rawGps.latitude !== prev?.latitude || rawGps.longitude !== prev?.longitude ) {
                    lastGpsValueRef.current = rawGps;
                } else {
                    if ( gpsPredictionService.getLastValue() === null ) {
                        rawGps = gpsPredictionService.predict( rawGps, Date.now() - rawGps.timestamp );
                    } else {
                        rawGps = gpsPredictionService.predict( null, 1.0 / 60.0 );
                    }
                }

                updateNavigationCursor( [ rawGps.longitude, rawGps.latitude ], rawCompass.heading );

                updateCamera( {
                    "pitch":    30,
                    "zoom":     17,
                    "center":   [ rawGps.longitude, rawGps.latitude ],
                    "bearing":  rawGps.heading,
                    "duration": 30,
                } );
            }
        },
        [ map, isFollowingNavigation, updateUserPositionMarker, updateNavigationCursor, updateCamera, isInteractingRef ],
    );

    /*
     * Register navigation animation with the centralized AnimationOrchestrator
     * FPS tracking is handled by the orchestrator via trackFps option
     */
    const { "fps": navigationFps } = useAnimationFrame(
        NAVIGATION_ANIMATION_CALLBACK_ID,
        navigationAnimationCallback,
        {
            "maxFps":   maxNavigationFps,
            "enabled":  handleAnimation && map !== null,
            "trackFps": true,
        },
    );

    /**
     * Manual calibration on user marker: align with nearest road (1st tap) or flip 180° (2nd tap).
     * If no road is found, calibrates to map north (0°).
     */
    const handleUserMarkerClick = useCallback( ( map: maplibregl.Map ) => {
        const position: [number, number] = [ rawGpsDataService.getLastValue().longitude, rawGpsDataService.getLastValue().latitude ];
        const routeSegments = extractRouteSegmentsFromMap( map, position );

        if ( routeSegments.length === 0 ) {
            compassCalibrationService.triggerManualCalibration( 0 );

            return;
        }

        const nearestRoadBearing = getNearestRoadBearing( position, routeSegments );
        if ( nearestRoadBearing === null ) {
            compassCalibrationService.triggerManualCalibration( 0 );

            return;
        }

        compassCalibrationService.triggerManualCalibration( nearestRoadBearing );
    }, [ extractRouteSegmentsFromMap ] );


    // Automatic camera update. Only in Navigation Mode
    useEffect( () => {
        if ( !map || !isMapReady ) {
            // console.log( "❌ [useMapNavigation] Missing map or map not ready, skipping mode update" );
            return;
        }
        if ( !map.isStyleLoaded() ) {
            return;
        }

        const position: [number, number] = [ rawGpsDataService.getLastValue().longitude, rawGpsDataService.getLastValue().latitude ];
        const heading = rawGpsDataService.getLastValue().speed < 2.78
            ?  rawCompassDataService.getLastValue().heading ?? 0
            : rawGpsDataService.getLastValue().heading ?? 0;

        if ( isFollowingNavigation ) {
            // console.log( "🧭 [useMapNavigation] Switching to NAVIGATION mode" );

            /*
             * Navigation mode: navigation cursor only
             * Use compass if speed < 10 km/h for smooth rotation
             */
            updateNavigationCursor( position, heading );
            updateCamera( {
                "pitch":    30,
                "zoom":     17,
                "center":   position,
                "bearing":  heading,
                "duration": 30,
            } );

            // Hide user marker and compass cone
            try {
                map.setLayoutProperty( "navigation-guide-animated-layer", "visibility", "visible" );
                map.setLayoutProperty( "user-heading-marker-layer", "visibility", "none" );
                map.setLayoutProperty( "user-location-layer", "visibility", "none" );

                // console.log( "✅ [useMapNavigation] NAVIGATION layers visibility updated successfully" );
            } catch( error ) {
                console.warn( "❌ [useMapNavigation] Error updating layer visibility:", error );
            }
        } else {
            /*
             * Exploration mode: cone is updated only by the animation callback to avoid
             * double updates (this effect + rAF) that made the cone jump then snap back.
             */
            try {
                map.setLayoutProperty( "navigation-guide-animated-layer", "visibility", "none" );
                map.setLayoutProperty( "user-heading-marker-layer", "visibility", "visible" );
                map.setLayoutProperty( "user-location-layer", "visibility", "visible" );

                // console.log( "✅ [useMapNavigation] NAVIGATION layers visibility updated successfully" );
            } catch( error ) {
                console.warn( "❌ [useMapNavigation] Error updating layer visibility:", error );
            }
        }
        lastNavigationModeRef.current = navigationMode;
    }, [ map, isMapReady, navigationMode, isFollowingNavigation, updateUserPositionMarker, updateNavigationCursor, handleAnimation, updateCamera ] );


    return {
        updateCamera,
        updateUserPositionMarker,
        updateNavigationCursor,
        handleUserMarkerClick,
        navigationFps,
        maxNavigationFps,
    };
};
